import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle, Clock, Circle, FileText } from "@phosphor-icons/react";

const FLOW = [
  ["submitted", "Submitted", "We have received your intent."],
  ["in_review", "Under Review", "We are reviewing your profile."],
  ["verified", "Due Diligence", "Verification in progress."],
  ["agreement_sent", "Agreement Sent", "Agreement sent for signing."],
  ["closed", "Approved", "Investment activated."],
];
const ORDER = FLOW.map((f) => f[0]);

const STATUS_BADGE = {
  submitted: "text-sky-300 bg-sky-500/10",
  in_review: "text-amber-300 bg-amber-500/10",
  verified: "text-violet-300 bg-violet-500/10",
  agreement_sent: "text-blue-300 bg-blue-500/10",
  closed: "text-emerald-300 bg-emerald-500/10",
  rejected: "text-rose-300 bg-rose-500/10",
};
const STATUS_LABEL = { submitted: "Submitted", in_review: "Under Review", verified: "Due Diligence", agreement_sent: "Agreement Sent", closed: "Approved", rejected: "Rejected" };

export default function Dashboard() {
  const { user } = useAuth();
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => api.get("/intents/me").then(({ data }) => { setIntents(data); setActive(data[0] || null); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const feesPaid = intents.filter((i) => i.payment_status === "paid").reduce((s, i) => s + (i.intent_fee || 0), 0);
  const approved = intents.filter((i) => i.dd_status === "closed").length;
  const brandsOfInterest = new Set(intents.map((i) => i.brand_slug)).size;

  const activeIdx = active ? ORDER.indexOf(active.dd_status) : -1;

  return (
    <div data-testid="dashboard-page" className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Dashboard</p>
          <h1 className="font-display text-4xl md:text-5xl text-white">Welcome back, {user?.name?.split(" ")[0] || "Partner"}!</h1>
          <p className="text-ebg-muted mt-2">Here's what's happening with your investments.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/explore" data-testid="dash-explore-cta" className="btn-gold px-5 py-2.5 rounded-md text-sm">Explore Franchises</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {[
          ["Applications", intents.length, "Total submitted"],
          ["Brands of Interest", brandsOfInterest, "Unique brands"],
          ["Intent Fees Paid", formatINR(feesPaid), `Across ${intents.filter(i=>i.payment_status==="paid").length} paid`],
          ["Approved", approved, "Due diligence cleared"],
        ].map(([l, v, sub]) => (
          <div key={l} className="bg-navy-900 border border-white/10 rounded-xl p-5">
            <p className="text-[0.65rem] uppercase tracking-widest text-ebg-muted">{l}</p>
            <p className="font-display text-3xl text-white mt-2">{v}</p>
            <p className="text-[0.7rem] text-ebg-muted mt-1">{sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-10">
        {/* Applications */}
        <div className="lg:col-span-3 bg-navy-900 border border-white/10 rounded-xl p-6">
          <h2 className="font-display text-2xl text-white mb-5">My Applications</h2>
          {loading ? <p className="text-ebg-muted">Loading…</p> : intents.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-lg p-10 text-center">
              <FileText size={32} className="text-ebg-muted mx-auto" />
              <p className="text-ebg-muted mt-3">No applications yet.</p>
              <Link to="/explore" className="inline-block mt-4 btn-gold px-6 py-2.5 rounded-md text-sm">Explore Franchises</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {intents.map((it) => (
                <button key={it.id} onClick={() => setActive(it)} data-testid={`intent-row-${it.ref}`}
                  className={`w-full text-left flex items-center justify-between gap-4 rounded-lg border p-4 transition-colors ${active?.id === it.id ? "border-gold/40 bg-gold/5" : "border-white/10 hover:border-white/25"}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="grid place-items-center w-10 h-10 rounded-md bg-navy-800 text-gold font-display text-lg">{it.brand_name?.[0]}</span>
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{it.brand_name}</p>
                      <p className="text-xs text-ebg-muted font-data">{it.ref} · {formatINR(it.ticket_amount)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {it.payment_status !== "paid"
                      ? <Link to={`/pay/${it.id}`} onClick={(e) => e.stopPropagation()} data-testid={`pay-intent-${it.ref}`} className="text-xs btn-gold px-3 py-1.5 rounded-md">Pay Intent Fee</Link>
                      : <span className="text-[0.6rem] uppercase tracking-wider text-emerald-400 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Fee paid</span>}
                    <span className={`text-[0.65rem] uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_BADGE[it.dd_status]}`}>{STATUS_LABEL[it.dd_status]}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Status flow */}
        <div className="lg:col-span-2 bg-navy-900 border border-white/10 rounded-xl p-6">
          <h2 className="font-display text-2xl text-white mb-1">Application Status Flow</h2>
          <p className="text-xs text-ebg-muted mb-6">{active ? active.brand_name : "Select an application"}</p>
          {active?.dd_status === "rejected" ? (
            <p className="text-sm text-rose-300">Due diligence did not clear — intent fee is refundable.</p>
          ) : (
            <div className="relative pl-2">
              {FLOW.map(([key, label, desc], i) => {
                const done = i < activeIdx, current = i === activeIdx;
                return (
                  <div key={key} className="flex gap-4 pb-7 last:pb-0 relative">
                    {i < FLOW.length - 1 && <span className={`absolute left-[11px] top-6 bottom-0 w-px ${done ? "bg-gold" : "bg-white/15"}`} />}
                    <span className="relative z-10 mt-0.5">
                      {done ? <CheckCircle size={24} className="text-gold" weight="fill" /> : current ? <Clock size={24} className="text-gold" weight="fill" /> : <Circle size={24} className="text-white/20" />}
                    </span>
                    <div>
                      <p className={`text-sm font-medium ${done || current ? "text-white" : "text-ebg-muted"}`}>{label}</p>
                      <p className="text-xs text-ebg-muted mt-0.5">{desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
